// Dimension distinctiveness — pure, no data/DOM.
//
// "Which sub-level is most interesting to drill next?" = which dimension, when
// you break THIS scope down by it, makes the scope look least like the org?
// That's an information-gain question: rank candidate dimensions by how far the
// scope's distribution over the dimension diverges from the tenant baseline's.
//
// We use Jensen-Shannon divergence (not raw KL): it's bounded [0,1], symmetric,
// handles zero-probability members cleanly (no smoothing needed), and — because
// it's bounded — compares fairly across dimensions of very different cardinality
// (raw KL inflates with more terms). High JSD ⇒ the scope uses this dimension in
// an unusual way ⇒ drilling it reveals structure specific to the scope. On a
// scope that mirrors the org (e.g. a dominant agent), every JSD is ~0 and the
// ranking correctly reports "nothing stands out".
//
// Distributions come from ranked top-K entries per side; the unaccounted tail on
// each side folds into an "__other__" bucket so the divergence still reflects
// how much of each distribution the shown members capture.

const OTHER = "__other__";

function klTermBits(p, m) {
  // p * log2(p/m); 0 when p == 0 (0·log0 ≡ 0).
  return p > 0 && m > 0 ? p * Math.log2(p / m) : 0;
}

/**
 * Jensen-Shannon divergence (base 2, in [0,1]) between two member→weight maps.
 * Each side is normalized to a probability distribution internally, so raw
 * activity counts are fine. Missing members are treated as zero probability.
 * @param {Record<string, number>} pW
 * @param {Record<string, number>} qW
 */
export function jsDivergence(pW, qW) {
  const pSum = Object.values(pW).reduce((s, v) => s + Math.max(0, v), 0);
  const qSum = Object.values(qW).reduce((s, v) => s + Math.max(0, v), 0);
  if (!(pSum > 0) || !(qSum > 0)) return 0;
  const keys = new Set([...Object.keys(pW), ...Object.keys(qW)]);
  let jsd = 0;
  for (const k of keys) {
    const p = Math.max(0, pW[k] || 0) / pSum;
    const q = Math.max(0, qW[k] || 0) / qSum;
    const m = (p + q) / 2;
    jsd += 0.5 * klTermBits(p, m) + 0.5 * klTermBits(q, m);
  }
  return Math.max(0, Math.min(1, jsd));
}

/**
 * Distinctiveness of breaking `scope` down by one dimension: JSD between the
 * scope's distribution over the dimension's members and the org's. Tail mass not
 * covered by the shown top-K entries folds into an "other" bucket on each side.
 *
 * @param {object} a
 * @param {Array<{id, activity}>} a.scopeEntries  top-K within the scope
 * @param {number} a.scopeTotal                   scope's total activity (denominator)
 * @param {Array<{id, activity}>} a.orgEntries    top-K within the tenant
 * @param {number} a.orgTotal                     org total activity
 * @returns {{ score: number, jsd: number, topShare: number }}
 */
export function distinctivenessScore({ scopeEntries = [], scopeTotal, orgEntries = [], orgTotal }) {
  if (!(scopeTotal > 0) || !(orgTotal > 0) || scopeEntries.length === 0) {
    return { score: 0, jsd: 0, topShare: 0 };
  }
  const p = {};
  const q = {};
  let pShown = 0;
  let qShown = 0;
  for (const e of scopeEntries) {
    const s = Math.max(0, Number(e.activity) || 0) / scopeTotal;
    p[String(e.id)] = s;
    pShown += s;
  }
  for (const e of orgEntries) {
    const s = Math.max(0, Number(e.activity) || 0) / orgTotal;
    q[String(e.id)] = s;
    qShown += s;
  }
  p[OTHER] = Math.max(0, 1 - pShown);
  q[OTHER] = Math.max(0, 1 - qShown);
  const jsd = jsDivergence(p, q);
  const topShare = Math.min(1, (Number(scopeEntries[0].activity) || 0) / scopeTotal);
  return { score: jsd, jsd, topShare };
}

// Below this JSD, the scope's breakdown of a dimension is effectively org-typical
// — no real signal. Used to gate the data-driven column reorder + suggestions so
// they don't churn on noise (fall back to the stable default ordering instead).
export const DISTINCT_MIN = 0.02;

/**
 * Rank scored dimensions most-distinctive first. `scored`: array of
 * { dimKey, score, ... } (any extra fields pass through). Ties break by dimKey
 * for stability. Pure.
 */
export function rankDistinctiveness(scored) {
  return [...(scored || [])].sort((a, b) => (b.score - a.score) || String(a.dimKey).localeCompare(String(b.dimKey)));
}
